import { Link } from "@tanstack/react-router";
import { MailWarning } from "lucide-react";
import { useAppSettings } from "@/hooks/use-app-settings";
import { useSession } from "@/lib/auth-client";
import { canEditAll } from "@/lib/permissions";

/**
 * Always-visible reminder that no email is reaching anybody.
 *
 * Sits above the header on every dashboard page, beside the impersonation
 * banner, for the same reason that one does: the state is invisible from the
 * screen you happen to be on, and the consequence of forgetting it is silent.
 * Somebody waiting on a review invitation that will never arrive has no way to
 * tell the difference between "diverted" and "broken" — so the app says which.
 *
 * Shown to EVERYONE signed in, not just admins. Anyone can be the person
 * wondering where their email went; only an admin can do anything about it, so
 * only an admin is offered the way out.
 */
export function TestEmailBanner() {
  const { data } = useAppSettings();
  const { data: session } = useSession();

  if (!data?.testEmailEnabled || !data.testEmailAddress) return null;
  const canFix = canEditAll(session?.user?.role);

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-amber-500/40 border-b bg-amber-500/15 px-4 py-2 text-sm md:px-6"
    >
      <p className="flex min-w-0 items-center gap-2">
        <MailWarning className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0">
          <span className="font-medium">Test mode — no email is reaching anyone.</span>{" "}
          <span className="text-muted-foreground">
            Everything Enable sends is going to{" "}
            <span className="font-medium text-foreground">{data.testEmailAddress}</span> instead of
            the people it is addressed to.
          </span>
        </span>
      </p>
      {canFix ? (
        <Link
          to="/dashboard/settings/config/email-testing"
          className="shrink-0 whitespace-nowrap rounded-md border border-border bg-background px-3 py-1.5 font-medium text-sm hover:bg-muted"
        >
          Turn it off
        </Link>
      ) : (
        // A standard user cannot change this, and a dead link that refuses them
        // is worse than being told who can.
        <span className="shrink-0 whitespace-nowrap text-muted-foreground text-sm">
          An admin can turn this off
        </span>
      )}
    </div>
  );
}
